import { useState, useCallback, useMemo } from 'react'
import { VersionReportVersionEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { VersionReportTemplateEntity } from '../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { TemplateMenu } from './TemplateMenu'
import { ReportMenu } from './ReportMenu'
import { useTemplateData } from '../../hooks/useTemplateData'
import { useReportData } from '../../hooks/useReportData'
import { NewVersionDialog } from '../dialog/NewVersionDialog'
import { TemplateDialog } from '../dialog/TemplateDialog'
import Slide from '@mui/material/Slide'
import { styled } from '@mui/material/styles'
import { colorPalette } from '../../../../style/colorPalette'

interface VersionSelectMenuProps {
  projectUuid: string
  open: boolean
  onClose: () => void
  onReportSelect: (report: VersionReportVersionEntity) => void
  onAfterCreateReport: (uuid: string) => void
}

export const VersionSelectMenu = ({
  projectUuid,
  open,
  onClose,
  onReportSelect,
  onAfterCreateReport,
}: VersionSelectMenuProps) => {
  const [isNewVersionDialogOpen, setIsNewVersionDialogOpen] =
    useState<boolean>(false)
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] =
    useState<boolean>(false)
  const [editingTemplate, setEditingTemplate] = useState<
    VersionReportTemplateEntity | undefined
  >()

  const {
    selectTemplate: selectTemplateData,
    resetTemplateSelection,
    selectedTemplate,
    loading: templateLoading,
    templates,
    refreshTemplates,
  } = useTemplateData()

  const {
    reports,
    loading: reportLoading,
    latestReport,
    handleAfterCreateReport: refreshReports,
  } = useReportData(selectedTemplate?.uuid)

  const openNewVersionDialog = useCallback(() => {
    setIsNewVersionDialogOpen(true)
  }, [])

  const closeNewVersionDialog = useCallback(() => {
    setIsNewVersionDialogOpen(false)
  }, [])

  const openTemplateDialog = useCallback(() => {
    setIsTemplateDialogOpen(true)
  }, [])

  const closeTemplateDialog = useCallback(() => {
    setIsTemplateDialogOpen(false)
    setEditingTemplate(undefined)
  }, [])

  const handleAfterCreateReportInternal = useCallback(
    async (uuid: string, _name: string) => {
      await refreshReports(uuid)
      onAfterCreateReport(uuid)
    },
    [onAfterCreateReport, refreshReports]
  )

  const handleTemplateUpdate = useCallback(
    (template: VersionReportTemplateEntity) => {
      setEditingTemplate(template)
      setIsTemplateDialogOpen(true)
    },
    []
  )

  const handleAfterSubmitTemplateInternal = useCallback(async () => {
    setIsTemplateDialogOpen(false)
    setEditingTemplate(undefined)
    await refreshTemplates()
  }, [refreshTemplates])

  const isReportMode = useMemo(() => !!selectedTemplate, [selectedTemplate])

  return (
    <>
      <StyledCollapse in={open} direction="right">
        <DialogContainer>
          {isReportMode ? (
            <ReportMenu
              templateName={selectedTemplate?.name || ''}
              reports={reports}
              loading={reportLoading}
              onReportSelect={onReportSelect}
              onBackToTemplate={resetTemplateSelection}
              onCreateNewReport={openNewVersionDialog}
              onClose={onClose}
            />
          ) : (
            <TemplateMenu
              templates={templates}
              loading={templateLoading}
              onTemplateSelect={selectTemplateData}
              onCreateNewTemplate={openTemplateDialog}
              onTemplateUpdate={handleTemplateUpdate}
              onClose={onClose}
            />
          )}
        </DialogContainer>
      </StyledCollapse>
      <NewVersionDialog
        open={isNewVersionDialogOpen}
        templateUuid={selectedTemplate?.uuid}
        onAfterSubmit={handleAfterCreateReportInternal}
        onClose={closeNewVersionDialog}
        latestVersionUuid={latestReport?.snapshotUuid}
      />
      <TemplateDialog
        projectUuid={projectUuid}
        open={isTemplateDialogOpen}
        onAfterSubmit={handleAfterSubmitTemplateInternal}
        onClose={closeTemplateDialog}
        original={editingTemplate}
      />
    </>
  )
}

const DialogContainer = styled('div')({
  width: '400px',
  height: '100%',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
})

const StyledCollapse = styled(Slide)({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 10,
  height: '100%',
})
